/**
 * VisibilityBadge + VisibilityToggle
 *
 * Compact UI primitive for the 'shared' vs 'private' visibility model.
 *
 * - <VisibilityBadge>   display-only pill (shows current visibility)
 * - <VisibilityToggle>  when the current user is the creator, a clickable
 *                       toggle that flips and persists via a callback
 *
 * Visual language:
 *   shared   → 👥 (users icon), soft grey/blue
 *   private  → 🔒 (lock icon),  amber, so it stands out
 *
 * Intentionally no external icon library — inline SVG keeps the bundle
 * clean and matches how other small components in this codebase render.
 */
import React, { useState } from 'react';

const VIS_LABELS = {
  shared:  { label: 'Shared',  help: 'Visible to your team' },
  private: { label: 'Private', help: 'Only visible to you' },
};

function IconUsers({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function IconLock({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function chipStyle(visibility, compact = false) {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    fontSize: compact ? 10 : 11, fontWeight: 600, letterSpacing: '0.02em',
    padding: compact ? '2px 7px' : '3px 9px',
    borderRadius: 12, userSelect: 'none',
  };
  if (visibility === 'private') {
    return { ...base, background: '#fef3c7', color: '#92400e' };
  }
  return { ...base, background: '#eef2ff', color: '#3730a3' };
}

export function VisibilityBadge({ visibility, compact = false, testid }) {
  const v = visibility === 'private' ? 'private' : 'shared';
  const { label, help } = VIS_LABELS[v];
  return (
    <span style={chipStyle(v, compact)} title={help}
          data-testid={testid || `visibility-badge-${v}`}>
      {v === 'private' ? <IconLock size={compact ? 10 : 11} /> : <IconUsers size={compact ? 10 : 11} />}
      {label}
    </span>
  );
}

/**
 * VisibilityToggle — click to flip, async persistence.
 *
 * Props:
 *   - visibility: 'shared' | 'private'
 *   - canEdit:    boolean (usually `is_mine` from the server)
 *   - onChange:   (next: 'shared' | 'private') => Promise<void>
 *   - compact:    smaller chip (for inline row usage)
 *   - testid:     data-testid override
 */
export default function VisibilityToggle({
  visibility, canEdit, onChange, compact = false, testid,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const v = visibility === 'private' ? 'private' : 'shared';

  // Not the creator → static badge only.
  if (!canEdit) {
    return <VisibilityBadge visibility={v} compact={compact} testid={testid} />;
  }

  const next = v === 'private' ? 'shared' : 'private';
  const { label, help } = VIS_LABELS[v];
  const hover = VIS_LABELS[next];

  const click = async () => {
    if (busy || !onChange) return;
    setBusy(true); setError(null);
    try {
      await onChange(next);
    } catch (e) { setError(e.message || 'Failed to change visibility'); }
    finally { setBusy(false); }
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <button
        type="button"
        onClick={click}
        disabled={busy}
        title={`${help}. Click to make it ${hover.label.toLowerCase()}.`}
        data-testid={testid || `visibility-toggle-${v}`}
        style={{
          ...chipStyle(v, compact),
          border: 'none', cursor: busy ? 'wait' : 'pointer',
          opacity: busy ? 0.6 : 1,
        }}
      >
        {v === 'private' ? <IconLock size={compact ? 10 : 11} /> : <IconUsers size={compact ? 10 : 11} />}
        {busy ? 'Saving…' : label}
      </button>
      {error && (
        <span style={{ fontSize: 10, color: 'var(--danger)' }} data-testid="visibility-toggle-error">
          {error}
        </span>
      )}
    </span>
  );
}
